<template>
  <div class="time-off-policy-members-setup">
    <!-- eslint-disable vue/no-mutating-props -->
    <div class="text-grey mb-15">Choose how you want to assign members to the time off policy</div>
    <div class="mb-15">
      <div class="d-flex">
        <label class="control-label required">Add members through</label>
        <i
          v-tooltip="tooltipOptions"
          :title="tooltipText"
          class="hi hi-info-filled hi-12 hi-tooltip text-grey ml-10"
        />
      </div>
      <AppSelect
        v-model="addMembersThroughOption"
        :options="addMembersMethodOptions"
        :select2-options="select2Options"
        data-testid="policy-members-setup-scope"
        @input="memberOptionChanged"
      />
    </div>
    <div
      v-if="addMembersThroughOption === '2'"
      class="dialog-warning blue bg-info empty-rules"
    >
      <i class="hi hi-info-filled hi-12"></i>
      <div>
        This policy is based on
        <strong>Home address - country</strong> and <strong>Employment type</strong> fields.
        <a
          class="clickable"
          target="_blank"
          :href="`/organizations/${organizationId}/members`"
        >
          Member profiles
        </a>
        must be up-to-date to ensure proper policy assignment.
      </div>
    </div>
    <div v-if="addMembersThroughOption === '2'">
      <!-- eslint-disable vue/prefer-true-attribute-shorthand -- shorthand would forward "" (falsy): the compat Provider takes validate-on-mount via attrs, not a declared Boolean prop -->
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        :validate-on-mount="true"
        :rules="`${hasInvalidRules ? 'required' : ''}`"
        :custom-messages="{ required: 'At least one member field must be selected to continue.' }"
        name="rules"
        :model-value="hasInvalidRules ? null : 'valid'"
      >
        <div class="row">
          <div class="col-md-6">
            <div>
              <label class="control-label required">Home Address - Country</label>
              <a
                class="pull-right select-all"
                @click="
                  () => {
                    $refs.countriesDropdown.selectAll();
                  }
                "
              >
                Select all
              </a>
              <AppSelect
                ref="countriesDropdown"
                v-model="timeOffPolicy.membership_rules.countries"
                :options="countries"
                :select2-options="countrySelect2Options"
                label="countries"
                compact
              />
            </div>
          </div>
          <div class="col-md-6">
            <label class="control-label required">Employment type</label>
            <a
              class="pull-right select-all"
              @click="
                () => {
                  $refs.employmentDropdown.selectAll();
                }
              "
            >
              Select all
            </a>
            <AppSelect
              ref="employmentDropdown"
              v-model="timeOffPolicy.membership_rules.employment_types"
              :options="employmentTypeOptions"
              :select2-options="employmentTypeSelect2Options"
              label="employment types"
              compact
            />
          </div>
        </div>
        <div
          v-if="errors[0]"
          class="d-flex align-items-center dialog-warning red mt-15 invalid-rules"
        >
          <i class="hi hi-warning hi-14 text-danger mr-10"></i>
          <div>{{ errors[0] }}</div>
        </div>
      </ValidationProvider>
      <!-- eslint-enable vue/prefer-true-attribute-shorthand -->
    </div>
    <div v-if="addMembersThroughOption === '1'">
      <div class="mb-15">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          name="time_off_policy_user_ids"
          :model-value="membersData.members"
        >
          <div :class="{ 'has-error': errors[0] }">
            <AppMembersSelector
              ref="members-user-selector"
              :organization-id="organizationId"
              :resource-id="timeOffPolicyId"
              :resource="resource"
              multiple
              show-select-all-trigger
              @submit-selections="updateMemberSelections"
            >
              <template #title>
                <label class="control-label">Members</label>
              </template>
            </AppMembersSelector>
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div
        v-if="showAddedMembers || showRemovedMembers"
        class="dialog-warning orange"
      >
        <i class="hi hi-info-filled hi-12 one-line"></i>
        <div>
          <span v-if="showAddedMembers">
            <a
              v-tooltip="warningTooltipOptions"
              class="clickable"
              href="#"
              :data-original-title="warningAddTooltip"
            >
              {{ pluralizedMembers(addedMembersCount) }}
            </a>
          </span>
          <span v-if="showAddedMembers && !showRemovedMembers"> will be added to this time off policy </span>
          <span v-if="showAddedMembers && showRemovedMembers"> will be added and </span>
          <span v-if="showRemovedMembers">
            <a
              v-tooltip="warningTooltipOptions"
              class="clickable"
              href="#"
              :data-original-title="warningRemoveTooltip"
            >
              {{ pluralizedMembers(removedMembers.length) }}
            </a>
            will be removed from this time off policy.
          </span>
          <span v-if="showRemovedMembers && hasPendingRequests">
            There are currently pending requests for this policy. Before removing members from the policy, approve or deny these requests otherwise they will remain in a submitted state.
          </span>
        </div>
      </div>
    </div>

    <div v-if="addMembersThroughOption === '3'">
      <MembersCsvUploader
        :uploader="uploadCsvFile"
        :changed="updateCsvMembers"
      />
    </div>

    <div
      v-if="addMembersThroughOption !== '2'"
      class="checkbox"
    >
      <AppCheckbox
        :value="timeOffPolicy.auto_add_new_users"
        @input="onAutoAddNewUsersChanged"
      >
        <span>Automatically add all new members to this policy</span>
      </AppCheckbox>
    </div>
  </div>
</template>

<script>
import { TimeOffPoliciesResource } from '~/resources';
import MembersCsvUploader from '../MembersCsvUploader.vue';
import { EMPLOYMENT_TYPE_OPTIONS } from '~/app_constants';

export default {
  name: 'TimeOffPolicyMembersSetupForm',
  components: { MembersCsvUploader },
  props: {
    timeOffPolicy: {
      type: Object,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    membersData: {
      type: Object,
      required: true,
    },
    showDialog: {
      type: Boolean,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    membersSelect2Options: {
      multiple: true,
      placeholder: 'Select member(s)',
      allowClear: true,
      width: '100%',
    },
    select2Options: Object.freeze({
      allowClear: false,
      multiple: false,
      width: '100%',
      placeholder: 'Select how to add members',
    }),
    countrySelect2Options: Object.freeze({
      allowClear: false,
      multiple: true,
      width: '100%',
      placeholder: 'Select home country',
    }),
    employmentTypeSelect2Options: Object.freeze({
      allowClear: false,
      multiple: true,
      width: '100%',
      placeholder: 'Select employment type',
    }),
    tooltipOptions: {
      placement: 'right',
    },
    tooltipText: 'Assign members in the most convenient way to set up a policy',
    addMembersThroughOption: 0,
    addedMembers: [],
    addedMembersCount: 0,
    removedMembers: [],
    showAddedMembers: false,
    showRemovedMembers: false,
    warningAddTooltip: '',
    warningRemoveTooltip: '',
    warningTooltipOptions: Object.freeze({
      placement: 'left',
      html: true,
    }),
  }),
  computed: {
    resource() {
      return TimeOffPoliciesResource.members;
    },
    timeOffPolicyId() {
      return this.timeOffPolicy.id ? Number(this.timeOffPolicy.id) : null;
    },
    disabledOptionTooltip() {
      return {
        disabledHint: 'Can’t change the way in which members were assigned since all data will be lost.',
        tooltipOptions: this.tooltipOptions,
      };
    },
    addMembersMethodOptions() {
      let listAndCsvDisabled = false;
      let rulesDisabled = false;

      if (this.timeOffPolicy.id) {
        if (this.timeOffPolicy.has_membership_rules === 'true') {
          listAndCsvDisabled = true;
        } else {
          rulesDisabled = true;
        }
      }

      return [
        {
          id: 1,
          text: 'List of members',
          description: 'List of all members belonging to the organization',
          disabled: listAndCsvDisabled,
          ...this.disabledOptionTooltip,
        },
        {
          id: 2,
          text: 'Home country and Employment type',
          description: 'Auto-assign by country and/or employment type fields from members profile',
          disabled: rulesDisabled,
          ...this.disabledOptionTooltip,
        },
        {
          id: 3,
          text: 'Import CSV',
          description: 'Upload CSV file',
          disabled: listAndCsvDisabled,
          ...this.disabledOptionTooltip,
        },
      ];
    },
    employmentTypeOptions() {
      return EMPLOYMENT_TYPE_OPTIONS;
    },
    hasInvalidRules() {
      const rules = this.timeOffPolicy.membership_rules;

      return (rules.countries || []).length === 0 && (rules.employment_types || []).length === 0;
    },
    hasPendingRequests() {
      const removedIds = (this.removedMembers || []).map((m) => m.user_id);
      const pendingIds = this.timeOffPolicy.member_with_pending_requests || [];

      return pendingIds.some((id) => removedIds.includes(id));
    },
  },
  created() {
    if (!this.timeOffPolicy.id) {
      this.addMembersThroughOption = '0';

      return;
    }

    this.addMembersThroughOption = this.timeOffPolicy.has_membership_rules === 'true' ? '2' : '1';
  },
  methods: {
    pluralizedMembers(count) {
      return count === 1 ? '1 member' : `${count} members`;
    },
    async updateMemberSelections({
      members,
      options = { select_all: false, clear_all: false },
      selectionCount,
      initialMembers,
    }) {
      /* eslint-disable vue/no-mutating-props */
      this.membersData.options = options;
      this.membersData.members = members;
      this.addedMembers = members.filter((member) => member.selected === true);
      this.removedMembers = members.filter((member) => member.selected === false);
      /* eslint-enable vue/no-mutating-props */

      if (options.select_all) {
        const reSelectedMemberCount = initialMembers.filter(
          (im) =>
            im.selected &&
            !members
              .filter((m) => !m.selected)
              .map((m) => m.user_id)
              .includes(im.id),
        ).length;

        this.addedMembersCount = selectionCount - reSelectedMemberCount;
      } else {
        this.addedMembersCount = this.addedMembers.length;
      }

      this.warningAddTooltip = this.addedMembers.map((m) => m.name).join('<br>');
      this.warningRemoveTooltip = this.removedMembers.map((m) => m.name).join('<br>');

      this.showAddedMembers = false;
      this.showRemovedMembers = false;

      if (this.addedMembers.length > 0 || this.addedMembersCount > 0) {
        this.showAddedMembers = true;
      }
      if (this.removedMembers.length > 0) {
        this.showRemovedMembers = true;
      }
    },
    async uploadCsvFile(form) {
      return await TimeOffPoliciesResource.checkCsv(
        { organization_id: this.organizationId, id: this.timeOffPolicy?.id },
        form,
      );
    },
    updateCsvMembers(userIds) {
      // eslint-disable-next-line vue/no-mutating-props
      this.membersData.user_ids = userIds;
    },
    onAutoAddNewUsersChanged(value) {
      this.$emit('policy-updated', 'auto_add_new_users', value);
    },
    memberOptionChanged(value) {
      this.$emit('member-options-updated', value);
    },
  },
};
</script>
